<template>
  <StatsWidget
    :title="$t('stats_dashboard.revenue_timeline.title')"
    :description="description"
    icon="insights"
    tone="green"
    :loading="widget.loading"
    :error="widget.error"
    :has-data="hasBuckets"
    :span-full="true"
    :empty-message="$t('stats_dashboard.empty_data')"
    @refresh="$emit('refresh')"
  >
    <div class="stats-revenue-timeline">
      <!-- HERO: verkocht — single inline metric strip + full-width chart -->
      <div class="stats-revenue-timeline__hero">
        <div class="stats-revenue-timeline__hero-strip">
          <div
            class="stats-revenue-timeline__switch"
            role="tablist"
            :aria-label="$t('stats_dashboard.revenue_timeline.title')"
          >
            <button
              v-for="option in heroOptions"
              :key="option.key"
              type="button"
              role="tab"
              class="stats-revenue-timeline__switch-option"
              :class="{ 'stats-revenue-timeline__switch-option--active': option.key === heroKey }"
              :aria-selected="option.key === heroKey"
              @click="heroKey = option.key"
            >
              <span
                class="stats-revenue-timeline__series-dot"
                :style="{ background: option.color }"
              />
              {{ option.label }}
            </button>
          </div>
          <span
            class="stats-revenue-timeline__info"
            tabindex="0"
            :aria-label="heroSeries.label"
            v-tippy="{ content: $t(heroSeries.tooltipKey) }"
          >
            <BaseIcon name="information-circle" size="13" />
          </span>
          <span class="stats-revenue-timeline__hero-value">
            {{ formatCurrencyCompact(heroTotal) }}
          </span>
          <span v-if="heroMeta" class="stats-revenue-timeline__hero-meta">
            {{ heroMeta }}
          </span>
        </div>
        <div class="stats-revenue-timeline__hero-chart">
          <Bar :data="heroChartData" :options="heroChartOptions" />
        </div>
      </div>

      <!-- Secondary lifecycle stages: besteld / gefactureerd / ontvangen -->
      <div class="stats-revenue-timeline__secondary">
        <div
          v-for="series in secondarySeries"
          :key="series.key"
          class="stats-revenue-timeline__card"
        >
          <div class="stats-revenue-timeline__card-header">
            <span class="stats-revenue-timeline__series-badge" :style="{ color: series.color }">
              <span
                class="stats-revenue-timeline__series-dot"
                :style="{ background: series.color }"
              />
              {{ series.label }}
            </span>
            <span
              v-if="series.ratio != null"
              class="stats-revenue-timeline__ratio"
              :title="
                $t('stats_dashboard.revenue_timeline.ratio_hint', {
                  ratio: formatPercent(series.ratio, { fractionDigits: 0 }),
                })
              "
            >
              {{ formatPercent(series.ratio, { fractionDigits: 0 }) }}
              {{ $t('stats_dashboard.revenue_timeline.of_sold') }}
            </span>
          </div>
          <div class="stats-revenue-timeline__card-value">
            {{ formatCurrencyCompact(series.total) }}
          </div>
          <div class="stats-revenue-timeline__card-chart">
            <Line :data="series.chartData" :options="secondaryChartOptions" />
          </div>
        </div>
      </div>
    </div>
  </StatsWidget>
</template>

<script>
  /**
   * Revenue timeline (2026-05-13).
   *
   * Layout:
   *   ┌──────────────────────────────────────────────────────────┐
   *   │ ● Verkocht  €803K  · piek mei (€278K) · gem. €161K/mnd   │
   *   │ ▮  ▮▮  ▮▮▮▮  ▮▮▮  ▮▮ ← full-width sold BAR chart (navy)  │
   *   ├──────────────────────────────────────────────────────────┤
   *   │ Besteld 82%    Gefactureerd 63%    Ontvangen 46%         │
   *   │ €660K          €503K               €370K                 │
   *   │ ╱⌒╲            ╱⌒╲                ╱⌒╲                    │
   *   │ ─── filled-area LINE charts ─── (no axes, no points)     │
   *   └──────────────────────────────────────────────────────────┘
   *
   * Hero uses bars because the headline metric is "what we sold per
   * bucket" — discrete-by-design. Secondary stages use filled-area
   * lines because the downstream lifecycle reads as a continuous
   * trend that lags the sold curve; bars there made the cards feel
   * cluttered.
   *
   * Lifecycle order: sold → ordered (besteld) → invoiced
   * (gefactureerd) → paid (ontvangen). The hero can be switched
   * between sold, planned and realized; the remaining series move to
   * the cards below. Ratios are always relative to sold.
   *
   * Colour palette is driven by `src/assets/scss/base/_variables.scss`
   * (brand / slate / amber / emerald). See the SERIES const for the
   * hex values mirrored here — Chart.js can't resolve CSS custom
   * properties from `<canvas>`.
   */
  import { computed, getCurrentInstance, ref } from 'vue';
  import { Bar, Line } from 'vue-chartjs';
  import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    Filler,
    Tooltip,
    Legend,
  } from 'chart.js';

  import StatsWidget from './StatsWidget.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import {
    formatCurrencyCompact,
    formatCurrencyPrecise,
    formatBucketLabel,
    formatPercent,
  } from '@/utils/statsFormat';

  ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    Filler,
    Tooltip,
    Legend
  );

  // Lifecycle stages in the order they appear on the dashboard.
  //
  // Colours mirror the project palette in `src/assets/scss/base/
  // _variables.scss` — Chart.js can't resolve CSS custom properties
  // from `<canvas>`, so we re-state the resolved hex. Keep in sync
  // if the palette ever changes.
  //
  //   sold     → --brand-950 (#172554)  — deep navy, hero (bars)
  //   ordered  → --slate-500 (#64748b)  — muted "in transit"
  //   invoiced → --amber-500 (#f59e0b)  — financial / paperwork
  //   paid     → --emerald-500 (#10b981)— cash collected
  //
  // The hero uses the deep navy on purpose: bright brand-500 dominated
  // the dashboard visually. Navy reads as the "anchor" series and lets
  // the downstream stages carry distinguishing colour weight.
  const SERIES = [
    {
      key: 'sold',
      labelKey: 'sold',
      color: '#172554',
      tooltipKey: 'stats_dashboard.kpis.sold_tooltip',
    },
    {
      key: 'planned',
      labelKey: 'planned',
      color: '#3b82f6',
      tooltipKey: 'stats_dashboard.kpis.planned_tooltip',
    },
    {
      key: 'realized',
      labelKey: 'realized',
      color: '#0ea5e9',
      tooltipKey: 'stats_dashboard.kpis.installed_tooltip',
    },
    {
      key: 'ordered',
      labelKey: 'ordered',
      color: '#64748b',
    },
    {
      key: 'invoiced',
      labelKey: 'invoiced',
      color: '#f59e0b',
    },
    {
      key: 'paid',
      labelKey: 'paid',
      color: '#10b981',
    },
  ];

  const HERO_KEYS = ['sold', 'planned', 'realized'];

  export default {
    name: 'StatsRevenueTimeline',

    components: { StatsWidget, BaseIcon, Bar, Line },

    props: {
      widget: { type: Object, required: true },
    },

    emits: ['refresh'],

    setup(props) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;

      const buckets = computed(() => props.widget.data?.buckets || []);
      const interval = computed(() => props.widget.data?.interval || 'daily');
      const hasBuckets = computed(() => buckets.value.length > 0);

      const description = computed(() => {
        const intervalLabel = $t(`stats_dashboard.revenue_timeline.interval.${interval.value}`);
        return $t('stats_dashboard.revenue_timeline.description', {
          interval: intervalLabel,
        });
      });

      const labels = computed(() =>
        buckets.value.map((b) => formatBucketLabel(b.bucket, interval.value))
      );

      const rawBuckets = computed(() => buckets.value.map((b) => b.bucket));

      // ============================================================
      // Per-series totals and chart data
      //
      // We precompute totals once so the small cards can show
      // "X% van verkocht" without iterating the buckets array per
      // render.
      // ============================================================

      const totals = computed(() =>
        SERIES.reduce((acc, s) => {
          acc[s.key] = buckets.value.reduce((sum, b) => sum + (Number(b[s.key]) || 0), 0);
          return acc;
        }, {})
      );

      // Date-formatted tooltip title — same shape as the old line
      // version so users keep the same recognition pattern.
      const tooltipTitle = (ctx) => {
        const idx = ctx?.[0]?.dataIndex ?? 0;
        const raw = rawBuckets.value[idx];
        if (!raw) return '';
        return new Intl.DateTimeFormat('nl-NL', {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        }).format(new Date(`${raw}T00:00:00`));
      };

      const baseChartOptions = (color) => ({
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: 'rgba(15, 23, 42, 0.95)',
            titleColor: '#fff',
            bodyColor: '#e2e8f0',
            padding: 10,
            callbacks: {
              title: tooltipTitle,
              label: (ctx) => `${ctx.dataset.label}: ${formatCurrencyPrecise(ctx.raw ?? 0)}`,
            },
          },
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: {
              color: '#94a3b8',
              maxRotation: 0,
              autoSkip: true,
              maxTicksLimit: 12,
              font: { size: 10 },
            },
          },
          y: {
            beginAtZero: true,
            border: { display: false },
            grid: { color: '#f1f5f9' },
            ticks: {
              color: '#94a3b8',
              callback: (value) => formatCurrencyCompact(value),
              font: { size: 10 },
            },
          },
        },
        // Sharp rectangular bars at ~40% of category width — reads as
        // "discrete data points" instead of an overflowing histogram.
        // The previous wider/rounded bars looked too "marketing slide"
        // for a dense ops dashboard.
        datasets: {
          bar: {
            borderRadius: 0,
            categoryPercentage: 0.7,
            barPercentage: 0.45,
          },
        },
        elements: {
          bar: {
            backgroundColor: color,
          },
        },
      });

      // ============================================================
      // Hero series (verkocht / gepland / gerealiseerd)
      // ============================================================

      const seriesLabel = (s) => $t(`stats_dashboard.revenue_timeline.legend.${s.labelKey}`);

      const heroKey = ref('sold');
      const heroOptions = computed(() =>
        SERIES.filter((s) => HERO_KEYS.includes(s.key)).map((s) => ({
          ...s,
          label: seriesLabel(s),
        }))
      );

      const heroSeries = computed(() => heroOptions.value.find((s) => s.key === heroKey.value));

      const heroTotal = computed(() => totals.value[heroKey.value] || 0);

      // Headline meta — peak bucket label + average per bucket. Helps
      // the user spot seasonality without staring at the chart.
      const heroMeta = computed(() => {
        if (!buckets.value.length) return null;
        let peak = buckets.value[0];
        let peakValue = -Infinity;
        for (const b of buckets.value) {
          const v = Number(b[heroKey.value]) || 0;
          if (v > peakValue) {
            peakValue = v;
            peak = b;
          }
        }
        const avg = heroTotal.value / buckets.value.length;
        const peakLabel = formatBucketLabel(peak.bucket, interval.value);
        const peakValueLabel = formatCurrencyCompact(peakValue);
        const avgLabel = formatCurrencyCompact(avg);
        const intervalLabel = $t(`stats_dashboard.revenue_timeline.interval.${interval.value}`);
        return $t('stats_dashboard.revenue_timeline.hero_meta', {
          peakLabel,
          peakValue: peakValueLabel,
          avg: avgLabel,
          interval: intervalLabel,
        });
      });

      const heroChartData = computed(() => ({
        labels: labels.value,
        datasets: [
          {
            label: heroSeries.value.label,
            data: buckets.value.map((b) => Number(b[heroKey.value]) || 0),
            backgroundColor: heroSeries.value.color,
            hoverBackgroundColor: heroSeries.value.color,
          },
        ],
      }));

      const heroChartOptions = computed(() => baseChartOptions(heroSeries.value.color));

      // ============================================================
      // Secondary series (besteld / gefactureerd / ontvangen)
      //
      // Each card carries its own mini chart. Sharing options between
      // them is fine — Chart.js merges per-dataset colour overrides,
      // so the tooltip / axis styling stays consistent while the bars
      // still take on the series colour.
      // ============================================================

      // Secondary mini-charts are filled-area line charts — picked
      // because the "downstream lifecycle" reads more naturally as a
      // continuous trend than as discrete bars (the underlying buckets
      // are dense and the values lag the sold curve, so the eye
      // wants a smooth shape to compare).
      const secondaryChartOptions = computed(() => ({
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: 'rgba(15, 23, 42, 0.95)',
            titleColor: '#fff',
            bodyColor: '#e2e8f0',
            padding: 8,
            callbacks: {
              title: tooltipTitle,
              label: (ctx) => `${ctx.dataset.label}: ${formatCurrencyPrecise(ctx.raw ?? 0)}`,
            },
          },
        },
        scales: {
          x: { display: false },
          y: { display: false, beginAtZero: true },
        },
        elements: {
          line: { borderJoinStyle: 'round' },
          point: { radius: 0, hoverRadius: 3 },
        },
      }));

      // Convert "#RRGGBB" → "#RRGGBB1F" (~12% alpha) for the fill.
      // 8-char hex avoids any rgba() bookkeeping when applying it.
      const withAlpha = (hex, alpha = '24') => `${hex}${alpha}`;

      const secondarySeries = computed(() =>
        SERIES.filter((s) => s.key !== heroKey.value).map((s) => {
          const total = totals.value[s.key] || 0;
          const sold = totals.value.sold || 0;
          const ratio = s.key !== 'sold' && sold > 0 ? (total / sold) * 100 : null;
          return {
            ...s,
            label: seriesLabel(s),
            total,
            ratio,
            chartData: {
              labels: labels.value,
              datasets: [
                {
                  label: $t(`stats_dashboard.revenue_timeline.legend.${s.labelKey}`),
                  data: buckets.value.map((b) => Number(b[s.key]) || 0),
                  borderColor: s.color,
                  backgroundColor: withAlpha(s.color),
                  fill: 'origin',
                  tension: 0.35,
                  borderWidth: 2,
                  pointRadius: 0,
                  pointHoverRadius: 3,
                },
              ],
            },
          };
        })
      );

      return {
        description,
        hasBuckets,
        heroKey,
        heroOptions,
        heroSeries,
        heroTotal,
        heroMeta,
        heroChartData,
        heroChartOptions,
        secondarySeries,
        secondaryChartOptions,
        formatCurrencyCompact,
        formatPercent,
      };
    },
  };
</script>

<style scoped lang="scss">
  @import '@/assets/scss/base/_mixins.scss';

  .stats-revenue-timeline {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
    flex: 1;
    min-height: 0;

    // ============================================================
    // Hero (verkocht) — compact metric strip + full-width chart
    // ============================================================

    &__hero {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    &__hero-strip {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      flex-wrap: wrap;
    }

    &__switch {
      display: inline-flex;
      padding: 0.2rem;
      gap: 0.2rem;
      background: var(--slate-100);
      border-radius: 0.5rem;
    }

    &__switch-option {
      display: inline-flex;
      align-items: center;
      gap: 0.4rem;
      padding: 0.3rem 0.65rem;
      border: none;
      border-radius: 0.375rem;
      background: transparent;
      color: var(--slate-500);
      @include fontSize('sm');
      font-weight: 600;
      cursor: pointer;
      transition:
        background 0.15s ease,
        color 0.15s ease;

      @include hover {
        color: var(--slate-700);
      }

      &:focus-visible {
        outline: 2px solid var(--brand-400);
        outline-offset: 1px;
      }

      &--active {
        background: white;
        color: var(--slate-800);
        @include shadow('xs');
      }
    }

    &__hero-value {
      font-size: 1.25rem;
      font-weight: 700;
      color: var(--slate-700);
      line-height: 1.1;
    }

    &__hero-meta {
      @include fontSize('sm');
      color: var(--slate-500);
    }

    &__hero-chart {
      position: relative;
      height: 220px;
      min-height: 220px;

      @include sm-down {
        height: 180px;
      }
    }

    // ============================================================
    // Secondary cards (besteld / gefactureerd / ontvangen)
    // ============================================================

    &__secondary {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(11.5rem, 1fr));
      gap: 0.75rem;
      padding-top: 0.75rem;
      border-top: 1px solid var(--slate-100);

      @include sm-down {
        grid-template-columns: 1fr;
      }
    }

    &__card {
      display: flex;
      flex-direction: column;
      gap: 0.4rem;
      padding: 0.85rem 0.95rem;
      background: var(--slate-50);
      border-radius: 0.625rem;
      border: 1px solid var(--slate-100);
    }

    &__card-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.5rem;
    }

    &__card-value {
      font-size: 1.25rem;
      font-weight: 700;
      color: var(--slate-700);
      line-height: 1;
    }

    &__card-chart {
      position: relative;
      height: 72px;
      margin-top: 0.25rem;
    }

    // ============================================================
    // Shared badge / dot / ratio styles
    // ============================================================

    &__series-badge {
      display: inline-flex;
      align-items: center;
      gap: 0.4rem;
      @include fontSize('sm');
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.03em;
    }

    &__series-dot {
      display: inline-block;
      width: 10px;
      height: 10px;
      border-radius: 3px;
    }

    &__info {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      color: var(--slate-400);
      cursor: help;
      transition: color 0.15s ease;

      @include hover {
        color: var(--slate-600);
      }

      &:focus-visible {
        outline: 2px solid var(--brand-400);
        outline-offset: 2px;
        border-radius: 0.25rem;
      }
    }

    &__ratio {
      @include fontSize('sm');
      color: var(--slate-500);
      font-weight: 500;
    }
  }
</style>
